import { GitCompareArrows } from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ProposalEvidence } from '@/components/schema/agent/ProposalEvidence'
import { ProposalFindings } from '@/components/schema/agent/ProposalFindings'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { SchemaProposal } from '@/services/api/schemaSessions'
import { DiffOperationBadge, ProposalDiffGroups } from './ProposalDiffGroups'
import { useProposalReviewNavigation } from './ProposalReviewNavigation'
import { proposalDiff } from './proposal-diff'
import { groupProposalDiff, type SchemaDiffGroup } from './proposal-diff-groups'

/** A review workspace, not a card embedded in a second scrolling conversation.
 * Only the active tab scrolls. Its decision bar always stays outside that area. */
export function ProposalDiffCard({
  base,
  proposal,
  reference,
}: {
  base: Record<string, unknown>
  proposal: SchemaProposal
  reference?: ReactNode
}) {
  const { t } = useTranslation()
  const { tab, setTab, region } = useReviewTab()
  const groups = useMemo(
    () =>
      groupProposalDiff(
        proposalDiff(base, {
          ...proposal.schema,
          name: proposal.name,
          description: proposal.description,
        })
      ),
    [base, proposal]
  )
  return (
    <section
      ref={region}
      tabIndex={-1}
      aria-label={t('schemaAgent.workbench.reviewChanges')}
      className="flex h-full min-h-0 flex-col bg-card outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
    >
      <Tabs value={tab} onValueChange={setTab} className="min-h-0 flex-1 gap-0">
        <ReviewHeader groups={groups} hasReference={!!reference} />
        <TabsContent
          value="changes"
          className="min-h-0 overflow-y-auto overscroll-contain p-4"
          data-testid="proposal-review-scroll"
        >
          <ReviewDetails proposal={proposal} groups={groups} />
        </TabsContent>
        {reference && (
          <TabsContent value="working" className="min-h-0 overflow-y-auto overscroll-contain p-4">
            {reference}
          </TabsContent>
        )}
      </Tabs>
    </section>
  )
}

function useReviewTab() {
  const [tab, setTab] = useState('changes')
  const region = useRef<HTMLElement>(null)
  const request = useProposalReviewNavigation()?.request ?? 0
  const seen = useRef(request)
  useEffect(() => {
    if (seen.current === request) return
    seen.current = request
    setTab('changes')
    region.current?.focus({ preventScroll: true })
    region.current?.scrollIntoView({ block: 'start' })
  }, [request])
  return { tab, setTab, region }
}

function ReviewHeader({
  groups,
  hasReference,
}: {
  groups: SchemaDiffGroup[]
  hasReference: boolean
}) {
  const { t } = useTranslation()
  return (
    <div className="shrink-0 space-y-3 border-b p-4">
      <div className="flex items-center gap-3">
        <GitCompareArrows className="size-5 shrink-0 text-primary" aria-hidden />
        <h2 className="flex-1 text-base font-semibold">
          {t('schemaAgent.workbench.reviewChanges')}
        </h2>
        <Badge variant="outline">{t('schemaAgent.workbench.notApplied')}</Badge>
      </div>
      <div role="group" className="flex flex-wrap gap-2" aria-label={t('schemaAgent.diff.summary')}>
        {(['add', 'remove', 'change'] as const).map(operation => {
          const count = groups.filter(group => group.operation === operation).length
          return count ? (
            <DiffOperationBadge key={operation} operation={operation} count={count} />
          ) : null
        })}
        {!groups.length && (
          <span className="text-xs text-muted-foreground">{t('schemaAgent.diff.unchanged')}</span>
        )}
      </div>
      <TabsList className="w-full" aria-label={t('schemaAgent.workbench.reviewView')}>
        <TabsTrigger value="changes">{t('schemaAgent.workbench.proposedChanges')}</TabsTrigger>
        {hasReference && (
          <TabsTrigger value="working">{t('schemaAgent.workbench.draft')}</TabsTrigger>
        )}
      </TabsList>
    </div>
  )
}

function ReviewDetails({
  proposal,
  groups,
}: {
  proposal: SchemaProposal
  groups: SchemaDiffGroup[]
}) {
  const { t } = useTranslation()
  return (
    <div className="space-y-5">
      {proposal.rationale && (
        <div className="space-y-1">
          <h3 className="text-xs font-medium text-muted-foreground">
            {t('schemaAgent.workbench.reasoning')}
          </h3>
          <p className="text-sm leading-relaxed">{proposal.rationale}</p>
        </div>
      )}
      {!!proposal.errors.length && <ProposalFindings proposal={proposal} />}
      <ProposalDiffGroups groups={groups} />
      <ProposalEvidence proposal={proposal} />
    </div>
  )
}
